import {
	Component,
	OnInit,
	inject,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	Validators,
	ReactiveFormsModule,
	FormsModule,
} from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from '@lmc-app/core/services/user.service';
import { AdminSettingsService } from '@lmc-app/core/services/admin-settings.service';
import { User } from '@lmc-app/shared/interface/user.interface';
import { AgreementPermissionKey } from '@lmc-app/core/constants/application-permissions.constant';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { UserOption } from '@lmc-app/shared/components/user-picker/user-picker.component';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel, MatError } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { NgFor, NgIf, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { UserPickerComponent } from '../../shared/components/user-picker/user-picker.component';

const SETTING_NAME = 'VALIDATION_MANAGER';

/** One card per permission; add an entry here (and its key on the backend) to add a card */
interface AgreementPermission {
	key: AgreementPermissionKey;
	label: string;
	icon: string;
	hint: string;
	disabledHint: string;
}

const AGREEMENT_PERMISSIONS: AgreementPermission[] = [
	{
		key: 'canCancelLpp',
		label: 'LPP Cancellation',
		icon: 'money_off',
		hint: 'Cancel LPP on an agreement for the selected users',
		disabledHint: 'Everyone is allowed to cancel LPP while this is off',
	},
	{
		key: 'canAmendLoan',
		label: 'Loan Amendment',
		icon: 'edit_note',
		hint: 'Send a loan amendment for the selected users',
		disabledHint:
			'Everyone is allowed to send a loan amendment while this is off',
	},
];

interface SaveQueue {
	inFlight: boolean;
	/** A change was made while saving; send the latest state once the current save ends */
	pending: boolean;
}

@Component({
	selector: 'app-agreement-settings',
	templateUrl: './agreement-settings.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./agreement-settings.component.scss'],
	imports: [
		MatCard,
		MatIcon,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		MatSelectSearchComponent,
		NgFor,
		NgIf,
		MatError,
		MatButton,
		MatProgressSpinner,
		MatSlideToggle,
		FormsModule,
		UserPickerComponent,
		AsyncPipe,
	],
})
export class AgreementSettingsComponent implements OnInit {
	private readonly _UserService = inject(UserService);
	private readonly _AdminSettingsService = inject(AdminSettingsService);
	private readonly _SnackBar = inject(MatSnackBar);

	managerForm = new FormGroup({
		manager: new FormControl(null, Validators.required),
		managerSearch: new FormControl(''),
	});

	allUsers: User[] = [];
	filteredUsers = new BehaviorSubject<User[]>([]);
	isSavingManager = false;

	readonly permissions = AGREEMENT_PERMISSIONS;
	permissionUsers: UserOption[] = [];
	isLoadingPermissions = false;
	/** User IDs granted each permission, including IDs of users no longer active */
	selected: Record<AgreementPermissionKey, number[]> = {
		canCancelLpp: [],
		canAmendLoan: [],
	};
	enabled: Record<AgreementPermissionKey, boolean> = {
		canCancelLpp: false,
		canAmendLoan: false,
	};
	/** Last state the server confirmed, restored when a save fails */
	private savedPermissions: Record<
		AgreementPermissionKey,
		{ ids: number[]; enable: boolean }
	> = {
		canCancelLpp: { ids: [], enable: false },
		canAmendLoan: { ids: [], enable: false },
	};
	private saveQueues: Record<AgreementPermissionKey, SaveQueue> = {
		canCancelLpp: { inFlight: false, pending: false },
		canAmendLoan: { inFlight: false, pending: false },
	};

	ngOnInit(): void {
		this.managerForm.get('managerSearch').valueChanges.subscribe((val) => {
			this.filterUsers(val);
		});

		this._UserService.getUsers().subscribe((res) => {
			this.allUsers = res;
			this.filteredUsers.next(this.allUsers.slice());
		});

		this._AdminSettingsService
			.getValidationManager(SETTING_NAME)
			.subscribe((res) => {
				const assignedManager = res?.setting_values;
				if (assignedManager) {
					this.managerForm.get('manager').setValue(assignedManager);
				}
			});

		this.loadPermissions();
	}

	filterUsers(val: string): void {
		if (!this.allUsers?.length) return;

		const search = (val || '').toLowerCase();
		const filtered = this.allUsers.filter((user) =>
			(user.full_name || '').toLowerCase().includes(search)
		);
		this.filteredUsers.next(filtered);
	}

	updateValidationManager() {
		if (this.managerForm.invalid) return;

		this.isSavingManager = true;

		this._AdminSettingsService
			.assignValidationManager(
				SETTING_NAME,
				this.managerForm.get('manager').value
			)
			.subscribe({
				next: () => {
					this.isSavingManager = false;
				},
				error: () => {
					this.isSavingManager = false;
				},
			});
	}

	/** Fetches the agreement permissions and employees, and records them as the last saved state */
	loadPermissions(): void {
		this.isLoadingPermissions = true;
		this._AdminSettingsService.getApplicationPermissionsConfig().subscribe({
			next: (res) => {
				const { permissions, employees } = res.data;
				this.permissionUsers = employees;
				AGREEMENT_PERMISSIONS.forEach(({ key }) => {
					this.savedPermissions[key] = {
						ids: [...(permissions[key]?.allowedEmployeeIds ?? [])],
						enable: permissions[key]?.enable === true,
					};
					this.restorePermission(key);
				});
				this.isLoadingPermissions = false;
			},
			error: (error) => {
				this.isLoadingPermissions = false;
				this.showMessage(
					error.error?.message || 'Failed to load agreement permissions'
				);
			},
		});
	}

	/** Stores the users picked for a permission and saves them right away */
	onUsersChange(key: AgreementPermissionKey, ids: number[]): void {
		this.selected[key] = ids;
		this.savePermission(key);
	}

	/** True while a save for this permission is in progress; drives the spinner on its card */
	isSaving(key: AgreementPermissionKey): boolean {
		return this.saveQueues[key].inFlight;
	}

	/**
	 * Saves a permission's toggle and selected users together, one save at a time so responses can't
	 * land out of order; changes made meanwhile go out in one follow-up save. On failure the card
	 * returns to the last saved state.
	 */
	savePermission(key: AgreementPermissionKey): void {
		const queue = this.saveQueues[key];
		if (queue.inFlight) {
			queue.pending = true;
			return;
		}
		queue.inFlight = true;
		const snapshot = {
			ids: [...this.selected[key]],
			enable: this.enabled[key],
		};
		this._AdminSettingsService
			.updateApplicationPermission(key, {
				allowedEmployeeIds: snapshot.ids,
				enable: snapshot.enable,
			})
			.subscribe({
				next: (res) => {
					this.savedPermissions[key] = snapshot;
					queue.inFlight = false;
					if (queue.pending) {
						queue.pending = false;
						this.savePermission(key);
					} else {
						this.showMessage(res.message);
					}
				},
				error: (error) => {
					queue.inFlight = false;
					queue.pending = false;
					this.restorePermission(key);
					const label = AGREEMENT_PERMISSIONS.find(
						(permission) => permission.key === key
					)?.label;
					this.showMessage(
						error.error?.message || `Failed to save ${label} permission`
					);
				},
			});
	}

	/** Puts a permission's toggle and users back to the last saved state */
	private restorePermission(key: AgreementPermissionKey): void {
		this.selected[key] = [...this.savedPermissions[key].ids];
		this.enabled[key] = this.savedPermissions[key].enable;
	}

	/** Shows a short dismissible snackbar */
	private showMessage(message: string): void {
		this._SnackBar.open(
			message,
			SnackbarConstants.ACTION_DISMISS,
			SnackbarConstants.DURATION_DISMISS_SHORT
		);
	}
}
